/**
 * Local voice-activity detection (VAD) for the phoneme capture path.
 *
 * The phoneme scorer records raw audio (`lib/audio-recorder.ts`) and — unlike
 * the on-device recognizer path (`lib/speech.ts`) — has no recognizer to derive
 * "the speaker stopped" from. To give the therapist web app the same tap-once →
 * speak → auto-stop feel the native patient app has, we watch the microphone's
 * energy IN THE BROWSER: an `AnalyserNode` on the live `MediaStream` yields the
 * waveform, we compute its RMS every ~50 ms, and end the capture once the level
 * has stayed quiet for a stability window after speech began.
 *
 * Nothing extra leaves the device: this reads only local audio energy. The only
 * audio that ever reaches the scorer is the final recorded clip, exactly as
 * before. Web-only by design — the native path keeps its own auto-stop and is
 * never wired to this (see `audio-recorder.ts`'s `isWeb` gate).
 *
 * Two pieces:
 *   - {@link createSpeechEndpointer} — a PURE state machine fed `(rms, nowMs)`
 *     samples. No Web Audio, so it's unit-testable in jsdom.
 *   - {@link attachVad} — the thin Web Audio glue that produces those samples
 *     from a `MediaStream` and fires the caller's hooks on a verdict.
 */

/**
 * Build-time switch to disable auto-stop without a code change if a mic/room
 * proves pathological. Default ON (unset ⇒ on); only an explicit "0"/"false"
 * turns it off, in which case the caller falls back to pure tap-to-stop. This
 * is orthogonal to `phonemeEvalEnabled` — the caller ANDs the two.
 */
export const vadEnabled: boolean =
  import.meta.env.VITE_PHONEME_VAD !== '0' && import.meta.env.VITE_PHONEME_VAD !== 'false'

/** Terminal verdict once, and only once, the machine decides the turn is over. */
export type EndpointerState =
  | 'waiting' // no speech onset yet (still within the pre-speech window)
  | 'speaking' // energy has crossed the onset threshold at least once
  | 'ended' // quiet for `silenceMs` after speech began — the normal stop
  | 'timeout-nospeech' // never crossed onset within `preSpeechMs`
  | 'timeout-safety' // total capture hit the absolute `maxMs` ceiling

/** Tunables. Defaults mirror the native constants in `lib/speech.ts`. */
export interface EndpointerOptions {
  /** Quiet gap after speech begins that commits the turn. */
  silenceMs?: number
  /** If no onset happens within this, give up ("never spoke"). */
  preSpeechMs?: number
  /** Absolute ceiling on total capture, regardless of energy. */
  maxMs?: number
  /** Initial window used only to sample the room's noise floor. */
  calibrationMs?: number
  /** Onset threshold never drops below this normalized-RMS floor. */
  absoluteFloor?: number
  /** Onset threshold = max(absoluteFloor, noiseFloor * this). */
  floorMultiplier?: number
}

const DEFAULTS: Required<EndpointerOptions> = {
  silenceMs: 800,
  preSpeechMs: 5000,
  maxMs: 8000,
  calibrationMs: 250,
  absoluteFloor: 0.015,
  floorMultiplier: 2.5,
}

export interface SpeechEndpointer {
  /**
   * Feed one RMS sample (normalized to [0, ~1], see {@link attachVad}) taken at
   * `nowMs` (any monotonic clock — first sample defines t=0). Returns the
   * current state; once a terminal state is reached it is latched and every
   * later call returns it unchanged.
   */
  push(rms: number, nowMs: number): EndpointerState
  /** Current state without advancing. */
  state(): EndpointerState
}

/**
 * Whether the machine has latched. Exported because the continuous-capture
 * recorder drives {@link createSpeechEndpointer} itself (it already has the
 * PCM frames, so it needs no analyser of its own) and has to recognise the
 * same verdicts {@link attachVad} does.
 */
export function isTerminalVadState(s: EndpointerState): boolean {
  return s === 'ended' || s === 'timeout-nospeech' || s === 'timeout-safety'
}

const isTerminal = isTerminalVadState

/**
 * The pure endpointing state machine. Keeps no Web Audio references — it just
 * consumes energy samples and timestamps, so the whole decision policy can be
 * exercised deterministically in tests by replaying a `(rms, t)` sequence.
 *
 * Policy:
 *   1. `calibrationMs` after the first sample is spent measuring the noise
 *      floor (max of the samples seen), then the onset threshold is frozen at
 *      `max(absoluteFloor, noiseFloor * floorMultiplier)`. Onset isn't detected
 *      during calibration so the initial mic settling / tap can't false-trigger.
 *   2. Any sample at/above the threshold marks speech as begun and refreshes
 *      the "last loud" time.
 *   3. Before onset: `preSpeechMs` without a loud sample ⇒ `timeout-nospeech`.
 *   4. After onset: `silenceMs` without a loud sample ⇒ `ended`.
 *   5. At any point `maxMs` of total capture ⇒ `timeout-safety`.
 */
export function createSpeechEndpointer(options: EndpointerOptions = {}): SpeechEndpointer {
  const opts = { ...DEFAULTS, ...options }

  let startMs: number | null = null
  let noiseFloor = 0
  let threshold: number | null = null // null until calibration completes
  let hasSpoken = false
  let lastLoudMs = 0
  let current: EndpointerState = 'waiting'

  function push(rms: number, nowMs: number): EndpointerState {
    if (isTerminal(current)) return current

    if (startMs === null) {
      startMs = nowMs
      lastLoudMs = nowMs
    }
    const elapsed = nowMs - startMs

    // Absolute backstop wins over everything.
    if (elapsed >= opts.maxMs) {
      current = 'timeout-safety'
      return current
    }

    // Calibration window: learn the noise floor, don't detect onset yet.
    if (threshold === null) {
      noiseFloor = Math.max(noiseFloor, rms)
      if (elapsed < opts.calibrationMs) {
        current = 'waiting'
        return current
      }
      threshold = Math.max(opts.absoluteFloor, noiseFloor * opts.floorMultiplier)
    }

    const loud = rms >= threshold
    if (loud) {
      hasSpoken = true
      lastLoudMs = nowMs
      current = 'speaking'
      return current
    }

    if (!hasSpoken) {
      current = elapsed >= opts.preSpeechMs ? 'timeout-nospeech' : 'waiting'
      return current
    }

    current = nowMs - lastLoudMs >= opts.silenceMs ? 'ended' : 'speaking'
    return current
  }

  return { push, state: () => current }
}

// --- Web Audio binding ----------------------------------------------------

/** Hooks the recorder/caller supplies; fired at most once per attach. */
export interface VadHooks {
  /** Normal end-of-speech OR the safety ceiling — caller should stop+score. */
  onEnd: () => void
  /** Pre-speech timeout ("never spoke"). Falls back to `onEnd` if omitted. */
  onNoSpeech?: () => void
}

/** How often we sample the analyser. 50 ms ≈ 20 Hz — plenty for endpointing. */
const POLL_MS = 50
/** Time-domain window size (power of two). */
const FFT_SIZE = 1024

type AudioContextCtor = typeof AudioContext
function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as typeof window & { webkitAudioContext?: AudioContextCtor }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/** Root-mean-square of a time-domain byte buffer, normalized to ~[0, 1]. */
function rmsOf(buffer: Uint8Array): number {
  let sumSq = 0
  for (let i = 0; i < buffer.length; i++) {
    const v = (buffer[i] - 128) / 128 // 0..255 centered at 128 → [-1, 1]
    sumSq += v * v
  }
  return Math.sqrt(sumSq / buffer.length)
}

/**
 * Watch `stream` for end-of-speech and fire the hooks once. Returns a teardown
 * that stops the poll loop and releases the Web Audio graph; it's idempotent
 * and safe to call from the hook itself. If the Web Audio API is unavailable or
 * setup throws, this is a no-op returning a no-op teardown — the caller's
 * manual stop still works, so no browser is left without a path.
 */
export function attachVad(
  stream: MediaStream,
  hooks: VadHooks,
  options: EndpointerOptions = {},
): () => void {
  const Ctx = getAudioContextCtor()
  if (Ctx === null) return () => {}

  let ctx: AudioContext | null = null
  let source: MediaStreamAudioSourceNode | null = null
  let analyser: AnalyserNode | null = null
  let timer: ReturnType<typeof setInterval> | null = null
  let torn = false

  function teardown(): void {
    if (torn) return
    torn = true
    if (timer !== null) clearInterval(timer)
    timer = null
    try {
      source?.disconnect()
    } catch {
      /* already gone */
    }
    try {
      analyser?.disconnect()
    } catch {
      /* already gone */
    }
    // close() returns a promise; we don't await — nothing depends on it.
    void ctx?.close().catch(() => {})
  }

  try {
    ctx = new Ctx()
    // On Chrome/Safari a context can start suspended; we're inside the mic tap
    // gesture, so resume is allowed. Ignore rejection — the analyser still runs.
    void ctx.resume().catch(() => {})
    source = ctx.createMediaStreamSource(stream)
    analyser = ctx.createAnalyser()
    analyser.fftSize = FFT_SIZE
    source.connect(analyser) // NOT to destination — we don't want to hear it
  } catch {
    teardown()
    return () => {}
  }

  const endpointer = createSpeechEndpointer(options)
  const buffer = new Uint8Array(analyser.fftSize)
  const clock = typeof performance !== 'undefined' ? () => performance.now() : () => Date.now()

  timer = setInterval(() => {
    if (torn || analyser === null) return
    analyser.getByteTimeDomainData(buffer)
    const state = endpointer.push(rmsOf(buffer), clock())
    if (!isTerminal(state)) return
    teardown() // stop polling before we hand control back
    if (state === 'timeout-nospeech') (hooks.onNoSpeech ?? hooks.onEnd)()
    else hooks.onEnd()
  }, POLL_MS)

  return teardown
}
